const NUMERIC_FANDOM_ID = /^\d+$/;

export type FandomSlugLookupStatus = 'found' | 'missing' | 'unavailable';

export type FandomSlugListSnapshot = {
  rows: ReadonlyArray<{ slug?: string | null }>;
  totalDocs: number;
};

/**
 * Legacy catalog filters use numeric fandom ids (`?fandoms=10529`).
 * Anything else in that param is a slug and must be checked against fandom data.
 */
export const collectFandomFilterSlugs = (values: readonly string[]): string[] => {
  const slugs: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || NUMERIC_FANDOM_ID.test(trimmed)) continue;
    slugs.push(trimmed);
  }
  return slugs;
};

const hasExactSlug = (
  rows: ReadonlyArray<{ slug?: string | null }>,
  normalizedSlug: string,
): boolean => {
  const expected = normalizedSlug.toLowerCase();
  return rows.some((row) => row.slug?.trim().toLowerCase() === expected);
};

/**
 * Decide whether a slug exists in the fetched fandom/universe lists.
 * A truncated page without an exact hit is inconclusive: do not treat it as missing,
 * otherwise a real slug buried past the limit would become a false 404.
 */
export const resolveFandomSlugLookupStatus = (
  slug: string,
  lists: readonly FandomSlugListSnapshot[],
): FandomSlugLookupStatus => {
  const normalized = slug.trim();
  if (!normalized) return 'missing';
  let hasTruncatedList = false;
  for (const list of lists) {
    if (hasExactSlug(list.rows, normalized)) return 'found';
    if (list.totalDocs > list.rows.length) hasTruncatedList = true;
  }
  if (hasTruncatedList) return 'unavailable';
  return 'missing';
};
